import { useEffect, useRef } from 'react';
import { SandboxBanner } from '../features/sandbox/sandbox-banner';
import { MaintenanceBanners } from './maintenance-banners/maintenance-banners';
import { SubscriptionBanner } from './subscription-banner';
import { AccountingBillingBanner } from './accounting-billing-banner';

/**
 * The one sticky stack above every shell, mounted by `RootLayout`: the sandbox
 * bar, then any maintenance / announcement bars, then the cancellation notice.
 *
 * Single wrapper rather than one sticky element per banner because the shells
 * size themselves against the viewport MINUS these bars. Two independently
 * sticky bars both at `top-0` would overlap, and each measuring its own
 * variable would leave the shells subtracting only one of them.
 *
 * Sticky rather than in-flow because on mobile the document is what scrolls;
 * on desktop the shells lock the viewport and it never moves.
 */
export function GlobalBanners() {
  const stackRef = useRef<HTMLDivElement>(null);

  // Publish the height the shells subtract from the viewport. Measured rather
  // than hardcoded: the text wraps to a second line on narrow screens, banners
  // come and go on a schedule, and a stale number either cuts every shell short
  // or lets it overflow.
  useEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;
    const root = document.documentElement;
    const observer = new ResizeObserver(([entry]) => {
      root.style.setProperty(
        '--global-banners-height',
        `${entry.target.getBoundingClientRect().height}px`,
      );
    });
    observer.observe(stack);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--global-banners-height');
    };
  }, []);

  return (
    <div ref={stackRef} className="sticky top-0 z-50 w-full">
      <SandboxBanner />
      <MaintenanceBanners />
      {/* Above every shell, onboarding included — that is exactly where a user
          lands after deleting their company, and where the cancellation would
          otherwise leave no trace at all. */}
      <SubscriptionBanner />
      {/* Paying companies using expenses/bank without the accounting line:
          « Ajouter Comptabilité ». */}
      <AccountingBillingBanner />
    </div>
  );
}
